import { Loader } from "@googlemaps/js-api-loader";
import { displayToast, sendAppsignalError, toRegExp } from "../modules/utils";

export default class AddressAutocomplete {
  constructor(node) {
    this.element = node;

    this.searchInputs = this.element.querySelectorAll(".js-autocomplete-search-field");
    this.addressLineOneInput = this.element.querySelector(".js-autocomplete-address-line-1");
    this.addressLineTwoInput = this.element.querySelector(".js-autocomplete-address-line-2"); // Street number
    this.postalInput = this.element.querySelector(".js-autocomplete-postal-code");
    this.localityInput = this.element.querySelector(".js-autocomplete-locality");
    this.countryInput = this.element.querySelector(".js-autocomplete-country");
    this.disabledInputs = this.element.querySelectorAll(".js-autocomplete-disabled");
    this.postalCodeError = this.element.querySelector(".js-autocomplete-postal-code-error");
    this.autocompleteToggle = this.element.querySelector(".js-autocomplete-toggle");
    this.autocompleteToggleWrapper = this.element.querySelector(".js-autocomplete-toggle-wrapper");
    this.postalInputWrapper = this.postalInput.parentElement;
    this.addressLineTwoInputWrapper = this.addressLineTwoInput.parentElement;

    this.searchInputsPlaceholders = [].slice.call(this.disabledInputs).map((input) => input.placeholder);
    this.errorMessage = this.element.dataset.errorMessage;
    this.noResultsFoundMessage = this.element.dataset.noResultsFoundMessage;
    this.errorGoogleMessage = this.element.dataset.errorGoogleMessage;
    this.initialResults = null;

    if (this.element.dataset.initialPostalCodeRegex === "") {
      this.postalCodeRegex = "";
    } else {
      this.postalCodeRegex = toRegExp(this.element.dataset.initialPostalCodeRegex);
    }

    this.enabled = this.postalCodeRegex != ""; // We only enable the autocomplete if a postal code regex is given.

    this.initialize();
  }

  initialize() {
    const language = document.documentElement.lang.toLowerCase();

    this.loader = new Loader({
      apiKey: "AIzaSyByf66YX7Z5FIhSCdHsWKwRPxVq855bOQA",
      version: "weekly",
      libraries: ["geocoding"],
      language: language,
    });

    window.addEventListener("google-maps-api-failure", () => {
      // We revert google's changes to the inputs caused by the authentication failure.
      this.enabled = false;
      displayToast(this.errorGoogleMessage, "warning");

      this.toggleDisabledInputs();
    });

    window.addEventListener("region-select-fetch-success", ({ detail }) => {
      if (detail === "") {
        this.postalCodeRegex = "";
      } else {
        this.postalCodeRegex = toRegExp(detail); // We update the postal code regex when the region changes
      }

      clearTimeout(this.timeout);
      this.enabled = this.postalCodeRegex != "";

      this.toggleDisabledInputs();
      this.searchInputs.forEach((input) => {
        input.classList.remove("is-invalid");
      });

      this.postalInputWrapper.classList.remove("input-validation-success");

      if (!this.enabled) {
        this.autocompleteToggleWrapper.classList.add("d-none");
      }

      this.handleAutocomplete(this.searchInputs[0]);
    });

    this.autocompleteToggle.addEventListener("click", () => {
      this.enabled = !this.enabled;

      this.disabledInputs.forEach((input) => {
        input.classList.remove("disabled");
        input.readOnly = false;
      });

      this.searchInputs.forEach((input) => {
        input.classList.remove("is-invalid");
        input.parentElement.classList.remove("input-validation-success");
      });

      this.autocompleteToggleWrapper.classList.add("d-none");
    });

    this.loader.load().then(() => {
      this.geocoderInstance = new google.maps.Geocoder();

      this.searchInputs.forEach((input) => {
        input.addEventListener("input", () => this.handleAutocomplete.bind(this)(input));
      });
    });
  }

  toggleDisabledInputs() {
    this.disabledInputs.forEach((input, index) => {
      input.classList.toggle("disabled", this.enabled);
      input.readOnly = this.enabled;
      input.placeholder = this.searchInputsPlaceholders[index]; // This can get reset by google's error handling

      if (!this.enabled) {
        input.value = "";
      }
    });
  }

  handleAutocomplete(input) {
    if (!this.enabled) return;

    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      if (input.value === "") {
        input.classList.remove("is-invalid");
        input.parentElement.classList.remove("input-validation-success");
        return;
      }

      this.searchInputs.forEach((searchInput) => {
        searchInput.classList.remove("is-invalid");
        searchInput.parentElement.classList.remove("input-validation-success");
      });

      if (this.postalInput.value === "") {
        // If the postal code is empty, we don't need to validate the format
        return;
      }

      this.validatePostalCodeFormat().then((valid) => {
        if (valid) {
          this.fetchAddress();
        } else {
          this.postalInput.classList.add("is-invalid");
          this.disabledInputs.forEach((input) => {
            input.value = "";
          });
        }
      });
    }, 1000); // Throttle the input to prevent too many requests
  }

  async validatePostalCodeFormat() {
    const regexResult = this.postalCodeRegex.test(this.postalInput.value);

    this.postalCodeError.classList.toggle("d-none", regexResult);

    if (regexResult) {
      // We only validate the postal code if the format is correct
      return await new Promise((resolve) => {
        this.geocoderInstance.geocode(
          {
            componentRestrictions: {
              country: this.countryInput.dataset.valueCode,
              postalCode: this.postalInput.value,
            },
          },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) {
              this.initialResults = results;
              this.postalInput.classList.remove("is-invalid");
              this.postalInputWrapper.classList.add("input-validation-success");
            } else {
              if (status === "ZERO_RESULTS") {
                this.postalInputWrapper.classList.remove("input-validation-success");
                this.postalInput.classList.add("is-invalid");
              } else {
                sendAppsignalError(status, "AddressAutocomplete", {
                  postalCode: this.postalInput.value,
                  country: this.countryInput.dataset.valueCode,
                  streetNumber: this.addressLineTwoInput.value,
                });
                displayToast(this.errorMessage);
              }
            }
            this.autocompleteToggleWrapper.classList.remove("d-none");
            resolve(status === google.maps.GeocoderStatus.OK);
          }
        );
      }).then((isValid) => isValid);
    }
  }

  async fetchAddress() {
    if (this.postalInput.value !== "" && this.addressLineTwoInput.value !== "") {
      return await new Promise((resolve, reject) => {
        this.geocoderInstance.geocode(
          {
            latLng: this.initialResults[0].geometry.location,
          },
          (results, status) => {
            if (status === google.maps.GeocoderStatus.OK) {
              resolve(results);
            } else {
              reject(status);
            }
          }
        );
      })
        .then((results) => {
          const streetname = results[0].address_components[1].long_name;
          return new Promise((resolve, reject) => {
            this.geocoderInstance.geocode(
              {
                address: `${streetname} ${this.addressLineTwoInput.value}`,
                componentRestrictions: { postalCode: this.postalInput.value, country: this.countryInput.dataset.valueCode },
              },
              (results, status) => {
                if (status === google.maps.GeocoderStatus.OK) {
                  resolve(results);
                } else {
                  reject(status);
                }
              }
            );
          });
        })
        .then((results) => {
          const { address_components } = results[0];
          this.addressLineTwoInputWrapper.classList.add("input-validation-success");
          this.fillInAddress(address_components);
        })
        .catch((status) => {
          this.addressLineTwoInput.classList.add("is-invalid");
          this.handleGeoCodeError(status);
        });
    }
  }

  handleGeoCodeError(status) {
    if (status === "ZERO_RESULTS") {
      displayToast(this.noResultsFoundMessage, "warning");
      this.disabledInputs.forEach((input) => {
        input.value = "";
      });
    } else {
      sendAppsignalError(status, "AddressAutocomplete", {
        postalCode: this.postalInput.value,
        country: this.countryInput.dataset.valueCode,
        streetNumber: this.addressLineTwoInput.value,
      });
      displayToast(this.errorMessage);
    }
  }

  fillInAddress(address) {
    for (const component of address) {
      const { types } = component;

      switch (types[0]) {
        case "route": {
          this.addressLineOneInput.value = component.short_name;
          break;
        }
        case "locality":
        case "administrative_area_level_2": {
          this.localityInput.value = component.long_name;
          break;
        }
      }
    }
  }
}
